import { supabase } from '@/lib/supabase';
import { emitCoreActivity } from '@/lib/coreEventHooks';
import { notifyMerchantNewOrder } from '@/lib/orderNotify';
import { triggerPushForUser } from '@/lib/notifyPush';
import { notificationTargetUrl } from '@/lib/pushDevices';
import type { CartItem } from '@/hooks/useCart';

// ─────────────────────────────────────────────────────────────
// Commande passée DIRECTEMENT depuis le chat IA (espace client).
// On réutilise la fonction atomique du Core `core_create_order_atomic`
// (même circuit que la boutique publique) : si une étape échoue, rien
// n'est créé. Le panier peut contenir des articles de plusieurs
// marchands → on regroupe par marchand et on crée une commande par
// marchand. Tout est journalisé dans le Core (best-effort).
// ─────────────────────────────────────────────────────────────

export interface ChatOrderBuyer {
  id: number;
  name?: string | null;
  email?: string | null;
  telephone?: string | null;
  /** Adresse de livraison indiquée par le client dans le chat. */
  address?: string | null;
  /** Ville de livraison (optionnelle). */
  city?: string | null;
}

export interface ChatOrderResult {
  ok: boolean;
  orderIds: number[];
  error?: string;
}

/**
 * Ligne de commande telle que stockée dans la table `commande` et utile
 * pour le suivi côté client (« où est ma commande ? »).
 */
export interface ClientOrderRow {
  id: number;
  titre: string | null;
  date_time: string | null;
  totalttc: string | null;
  sp: string | null;
  quantite: number | null;
  monaie: string | null;
  statut_com_client: string | null;
  statut_com_vendeur: string | null;
  details: string | null;
  items_json: Array<{ id?: number | string; name?: string; quantity?: number | string; price?: number | string }> | null;
}

/**
 * Récupère l'historique des commandes d'un client (pour le suivi dans le chat).
 * On rapproche la commande du compte connecté par id, email ou téléphone —
 * exactement comme la page « Mon compte ».
 */
export async function fetchClientOrders(buyer: ChatOrderBuyer, limit = 10): Promise<ClientOrderRow[]> {
  const filters: string[] = [];
  if (buyer.id != null) filters.push(`user_id.eq.${buyer.id}`);
  if (buyer.email) {
    filters.push(`email.eq.${buyer.email}`);
    filters.push(`user_id.eq.${buyer.email}`);
  }
  if (buyer.telephone) {
    filters.push(`tel.eq.${buyer.telephone}`);
    filters.push(`user_id.eq.${buyer.telephone}`);
  }

  let query = supabase
    .from('commande')
    .select('id, titre, date_time, totalttc, sp, quantite, monaie, statut_com_client, statut_com_vendeur, details, items_json')
    .order('date_time', { ascending: false })
    .limit(limit);
  if (filters.length > 0) query = query.or(filters.join(','));

  const { data } = await query;
  return (data as ClientOrderRow[]) || [];
}

export async function placeOrderFromCart(params: {
  items: CartItem[];
  currency: string;
  buyer: ChatOrderBuyer;
}): Promise<ChatOrderResult> {
  const { items, currency, buyer } = params;
  if (items.length === 0) return { ok: false, orderIds: [], error: 'panier_vide' };

  const buyerAddress = (buyer.address || '').trim();
  const buyerCity = (buyer.city || '').trim();

  // Regroupement par marchand (owner_id du produit).
  const groups = new Map<number, CartItem[]>();
  for (const item of items) {
    const ownerId = item.product.owner_id ?? 0;
    const bucket = groups.get(ownerId);
    if (bucket) bucket.push(item);
    else groups.set(ownerId, [item]);
  }

  const orderIds: number[] = [];
  const buyerName = buyer.name || 'Client';
  const buyerEmail = buyer.email || '';
  const buyerPhone = buyer.telephone || '';
  const cleanNotes = "Commande passée via l'assistant IA";

  try {
    for (const [ownerId, group] of groups.entries()) {
      // La devise de référence reste celle DU PRODUIT (monnaie du marchand) ;
      // on ne retombe sur la devise passée en paramètre que si le produit n'en
      // a pas.
      const orderCurrency = group[0].product.currency || currency;
      const subtotal = group.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
      const totalCount = group.reduce((sum, i) => sum + i.quantity, 0);
      const details = group.map((i) => `${i.product.name} ×${i.quantity}`).join(', ');
      const ownerName = group[0].product.owner_name || 'boutique';

      const orderPayload = {
        idvendeur: ownerId,
        owner: ownerId,
        titre: `Commande ${ownerName}`,
        typecommande: 'produit',
        serviceouproduit: 1,
        nomclient: buyerName,
        email: buyerEmail,
        tel: buyerPhone,
        adresse: buyerAddress,
        whatsapp: buyerPhone,
        details,
        user_id: String(buyer.id),
        monaie: orderCurrency,
        methodepayment: 'A convenir',
        totalht: String(subtotal),
        totalttc: String(subtotal),
        quantite: totalCount,
        statut_com_vendeur: 'En attente',
        statut_com_client: 'En attente',
        ville: buyerCity,
        noteclient: cleanNotes,
        items_json: group.map((i) => ({
          id: i.product.id,
          name: i.product.name,
          quantity: i.quantity,
          product_type: i.product.product_type || 'physical',
          price: i.product.price,
        })),
        currency: orderCurrency,
        subtotal_items: subtotal,
        tax_total: null,
        customer_notes: cleanNotes,
        recipient: {
          name: buyerName,
          email: buyerEmail,
          phone: buyerPhone,
          address: buyerAddress,
          city: buyerCity,
        },
        customer_id: String(buyer.id),
        items: group.map((i) => ({
          product_id: String(i.product.id),
          product_name: i.product.name,
          quantity: i.quantity,
          unit_price: i.product.original_price ?? i.product.price,
          final_price: i.product.price,
          subtotal: i.product.price * i.quantity,
        })),
        notify_user_id: ownerId,
        notify_sender_id: buyer.id,
        notify_message: `Nouvelle commande via l'assistant IA — ${totalCount} article${totalCount > 1 ? 's' : ''} — ${subtotal.toLocaleString()} ${orderCurrency}`,
      };

      const { data: rpcData, error: rpcError } = await supabase.rpc('core_create_order_atomic', {
        p_org_id: null,
        p_tenant_id: ownerId || null,
        p_order: orderPayload,
      });
      if (rpcError) throw rpcError;

      const created = (rpcData || {}) as { commande_id?: number };
      const orderId = created.commande_id ?? null;
      if (orderId) orderIds.push(orderId);

      // E-mail + WhatsApp au marchand (best-effort, en plus du push interne).
      void notifyMerchantNewOrder({
        commerceId: ownerId,
        orderId,
        customerName: buyerName,
        items: group.map((i) => ({ name: i.product.name, quantity: i.quantity })),
        total: subtotal,
        currency: orderCurrency,
      });
      // 📲 Notification PWA (Web Push) au marchand, même app fermée.
      // Cible la page des commandes (et la commande concernée) au clic.
      void triggerPushForUser(ownerId, {
        title: 'Nouvelle commande',
        body: `Commande via l'assistant IA — ${totalCount} article${totalCount > 1 ? 's' : ''} — ${subtotal.toLocaleString()} ${orderCurrency}`,
        url: notificationTargetUrl('order', { publicationId: orderId }),
      });

      // Journalisation Core (Event Bus + Timeline) — best-effort : un échec ne
      // doit jamais faire échouer la commande, déjà créée en base.
      try {
        await emitCoreActivity({
          commerceId: ownerId || null,
          eventKey: 'order.created',
          entityType: 'order',
          entityId: orderId ?? 0,
          title: `Commande #${orderId ?? ''} passée via l'assistant IA`,
          description: details,
          amount: subtotal,
          currency: orderCurrency,
          payload: { customer: buyerName, items: totalCount, channel: 'ai_chat' },
          actorName: buyerName,
          actorUserId: buyer.id,
          source: 'ai_chat',
        });
      } catch {
        /* le socle est best-effort : la commande reste valide */
      }
    }

    return { ok: true, orderIds };
  } catch (err: unknown) {
    return {
      ok: false,
      orderIds,
      error: err instanceof Error ? err.message : 'Erreur inconnue',
    };
  }
}